/**
 * Core implementation for browser automation tasks
 */

import { fetchWithRetry, withTimeout } from '../utils/resilience.js';
import { logger } from '../../logger.js';
import type {
  BrowserConfig,
  BrowserTaskInput,
  BrowserTaskInputWithSchema,
  BrowserTaskResult,
  BrowserTaskWithPromise,
  BrowserTaskWithPromiseAndSchema,
  RecordingStatus,
  RecordingWithMethods,
  ErrorsResponse,
  LiveSessionOptions,
  IframeOptions,
  WebpOptions,
  WebpResponse,
} from './types.js';
import { buildLiveUrl, buildLiveIframe, buildEmbedCode, resolvePreset } from './live.js';
import { ProfilesClient } from './profiles/core.js';
import { MorphAPIClient } from '../../core/client.js';
import { APIResource } from '../../core/resource.js';

const DEFAULT_CONFIG = {
  apiUrl: process.env.MORPH_ENVIRONMENT === 'DEV' 
    ? 'http://localhost:8000'
    : 'https://browser.morphllm.com',
  timeout: 1000000, // 10 minutes for complex tasks
  debug: false,
};

/**
 * BrowserClient class for easier usage with instance configuration
 *
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).browser`).
 * Standalone clients remain only for backwards compatibility and may be removed in a future
 * major version — do not use them in new code.
 */
export class BrowserClient extends APIResource {
  private config: BrowserConfig;

  /**
   * Profile management - create and manage browser profiles for storing login state.
   */
  public profiles: ProfilesClient;

  constructor(clientOrConfig: MorphAPIClient | BrowserConfig = {}) {
    const isClient = clientOrConfig instanceof MorphAPIClient;
    super(
      isClient
        ? clientOrConfig
        : new MorphAPIClient({
            apiKey: clientOrConfig.apiKey,
            browserURL: clientOrConfig.apiUrl,
            timeout: clientOrConfig.timeout,
            retryConfig: clientOrConfig.retryConfig,
            debug: clientOrConfig.debug,
          }),
    );
    // Browser is a distinct service: keep its own wire (raw fetch in the
    // module functions below) but source the host/key/retries from the shared
    // transport so configuration lives in one place.
    this.config = isClient
      ? {
          ...DEFAULT_CONFIG,
          apiUrl: clientOrConfig.browserURL,
          apiKey: clientOrConfig.resolveApiKey(),
          retryConfig: clientOrConfig.retryConfig,
          debug: clientOrConfig.debug,
        }
      : { ...DEFAULT_CONFIG, ...clientOrConfig };
    this.profiles = new ProfilesClient(this.config);
  }

  /**
   * Execute a browser automation task
   */
  async execute(input: BrowserTaskInput): Promise<BrowserTaskResult> {
    return executeBrowserTask(input, this.config);
  }

  async createTask(input: BrowserTaskInput): Promise<BrowserTaskWithPromise>;
  async createTask<T>(input: BrowserTaskInputWithSchema<T>): Promise<BrowserTaskWithPromiseAndSchema<T>>;
  async createTask<T>(
    input: BrowserTaskInput | BrowserTaskInputWithSchema<T>
  ): Promise<BrowserTaskWithPromise | BrowserTaskWithPromiseAndSchema<T>> {
    const apiUrl = this.config.apiUrl || DEFAULT_CONFIG.apiUrl;
    const debug = this.config.debug || false;
    
    const hasTask = typeof input.task === 'string' && input.task.trim().length > 0;
    const hasDiff = typeof (input as any).diff === 'string' && (input as any).diff.trim().length > 0;

    if (!hasTask && !hasDiff) {
      throw new Error('Browser task requires either "task" (natural language) or "diff" (PR-review planning)');
    }

    logger.debug('Browser', 'create_task', { task: (input.task ?? '').slice(0, 100), url: input.url, endpoint: `${apiUrl}/browser-task/async` });
    
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.config.apiKey) headers['Authorization'] = `Bearer ${this.config.apiKey}`;
    
    // Call ASYNC endpoint for immediate return with live URL
    const response = await fetch(`${apiUrl}/browser-task/async`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        task: input.task,
        diff: input.diff,
        url: input.url,
        max_steps: input.maxSteps ?? 10,
        model: input.model ?? 'morph-computer-use-v0',
        viewport_width: input.viewportWidth ?? 1280,
        viewport_height: input.viewportHeight ?? 720,
        external_id: input.externalId,
        repo_id: input.repoId,
        repo_full_name: input.repoFullName,
        commit_id: input.commitId,
        record_video: input.recordVideo ?? false,
        video_width: input.videoWidth ?? input.viewportWidth ?? 1280,
        video_height: input.videoHeight ?? input.viewportHeight ?? 720,
        allow_resizing: input.allowResizing ?? false,
        structured_output: 'schema' in input ? stringifyStructuredOutput(input.schema) : undefined,
        auth: input.auth,
        profile_id: input.profileId,
        force_query_params: input.forceQueryParams,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      logger.error('Browser', 'create_task_error', { status: response.status, error: errorText });
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = mapTaskResult(await response.json());
    logger.debug('Browser', 'task_created', { recording_id: result.recordingId, task_id: result.taskId, debug_url: !!result.debugUrl });
    
    if ('schema' in input) {
      return wrapTaskResponseWithSchema(result, this.config, input.schema);
    } else {
      return wrapTaskResponse(result, this.config);
    }
  }

  /**
   * Execute task with recording and wait for video to be ready
   */
  async executeWithRecording(
    input: BrowserTaskInput & { recordVideo: true }
  ): Promise<BrowserTaskResult & { recording?: RecordingWithMethods }> {
    return executeWithRecording(input, this.config);
  }

  /**
   * Get recording status and URLs
   */
  async getRecording(recordingId: string): Promise<RecordingWithMethods> {
    return getRecording(recordingId, this.config);
  }

  /**
   * Wait for recording to complete with automatic polling
   */
  async waitForRecording(
    recordingId: string,
    options?: { timeout?: number; pollInterval?: number }
  ): Promise<RecordingWithMethods> {
    return waitForRecording(recordingId, this.config, options);
  }

  /**
   * Get errors from recording with screenshots
   */
  async getErrors(recordingId: string): Promise<ErrorsResponse> {
    return getErrors(recordingId, this.config);
  }

  /**
   * Get animated WebP preview of recording
   */
  async getWebp(
    recordingId: string,
    options?: WebpOptions
  ): Promise<WebpResponse> {
    return getWebp(recordingId, this.config, options);
  }

  /**
   * Check if browser worker service is healthy
   */
  async checkHealth(): Promise<{
    ok: boolean;
    google_configured: boolean;
    database_configured: boolean;
    s3_configured: boolean;
    error?: string;
  }> {
    return checkHealth(this.config);
  }
}

/**
 * Execute a natural language browser automation task
 * 
 * Returns the full task result including rich agent history data (urls, errors, 
 * action_history, judgement, etc.). When using this as an agent tool, use the
 * formatResult() functions from the SDK adapters to return a concise summary.
 * 
 * @param input - Task parameters
 * @param config - Optional configuration (apiKey, apiUrl to override default)
 * @returns Task result with success status, findings, and comprehensive execution history
 * 
 * @example
 * ```typescript
 * const result = await executeBrowserTask(
 *   {
 *     task: "Test checkout flow for buying a pineapple",
 *     url: "https://3000-abc.e2b.dev",
 *     maxSteps: 20,
 *     repoId: "my-project",
 *     commitId: "uuid-here"
 *   },
 *   {
 *     apiKey: process.env.MORPH_API_KEY,
 *     // apiUrl: 'http://localhost:8001' // Override for local testing
 *   }
 * );
 * 
 * if (result.success) {
 *   console.log('Task completed:', result.result);
 *   console.log('URLs visited:', result.urls);
 *   console.log('Actions taken:', result.actionNames);
 *   console.log('Has errors:', result.hasErrors);
 *   console.log('Replay:', result.replayUrl);
 * }
 * ```
 */
export async function executeBrowserTask(
  input: BrowserTaskInput,
  config: BrowserConfig = {}
): Promise<BrowserTaskResult> {
  const apiUrl = config.apiUrl || DEFAULT_CONFIG.apiUrl;
  const timeout = config.timeout || DEFAULT_CONFIG.timeout;
  const debug = config.debug || false;

  if (!input.task || input.task.trim().length === 0) {
    return { 
      success: false, 
      error: 'Task description is required. Example: "Go to example.com and click the login button"' 
    };
  }

  if (input.maxSteps !== undefined && (input.maxSteps < 1 || input.maxSteps > 50)) {
    return { 
      success: false, 
      error: 'maxSteps must be between 1 and 50. Use more steps for complex multi-page flows.'
    };
  }

  logger.debug('Browser', 'execute_start', { task: input.task.slice(0, 100), url: input.url, max_steps: input.maxSteps ?? 10, record_video: input.recordVideo ?? false });

  const startTime = Date.now();

  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (config.apiKey) headers['Authorization'] = `Bearer ${config.apiKey}`;

    const fetchPromise = fetchWithRetry(
      `${apiUrl}/browser-task`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          task: input.task,
          url: input.url,
          max_steps: input.maxSteps ?? 10,
          model: input.model ?? 'morph-computer-use-v0',
          viewport_width: input.viewportWidth ?? 1280,
          viewport_height: input.viewportHeight ?? 720,
          external_id: input.externalId,
          repo_id: input.repoId,
          commit_id: input.commitId,
          record_video: input.recordVideo ?? false,
          video_width: input.videoWidth ?? input.viewportWidth ?? 1280,
          video_height: input.videoHeight ?? input.viewportHeight ?? 720,
          allow_resizing: input.allowResizing ?? false,
          structured_output: input.structuredOutput,
          auth: input.auth,
          profile_id: input.profileId,
          force_query_params: input.forceQueryParams,
        }),
      },
      config.retryConfig
    );

    const response = await withTimeout(
      fetchPromise,
      timeout,
      `Browser task timed out after ${timeout}ms. Consider increasing timeout or reducing maxSteps.`
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      logger.error('Browser', 'execute_error', { status: response.status, error: errorText });
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result: BrowserTaskResult = mapTaskResult(await response.json());
    const elapsed = Date.now() - startTime;
    logger.debug('Browser', 'execute_complete', { success: result.success, latency_ms: elapsed, steps: result.stepsTaken ?? 0, recording_id: result.recordingId });

    return result;

  } catch (error) {
    if (error instanceof Error) {
      // Handle network errors
      if (error.message.includes('ECONNREFUSED') || error.message.includes('fetch failed')) {
        return {
          success: false,
          error: `Cannot connect to browser worker at ${apiUrl}. Ensure the service is running and accessible. For local dev, set MORPH_ENVIRONMENT=DEV.`,
        };
      }

      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: false,
      error: String(error),
    };
  }
}

/**
 * Get recording status and video URL
 * 
 * @param recordingId - Recording UUID from BrowserTaskResult
 * @param config - Configuration with apiKey
 * @returns Recording with convenience methods (.getWebp(), .getErrors())
 * 
 * @example
 * ```typescript
 * const recording = await getRecording('uuid-here', { apiKey: 'key' });
 * 
 * // Get animated WebP
 * const { webpUrl } = await recording.getWebp({ width: 780 });
 * 
 * // Get errors with screenshots
 * const { errors } = await recording.getErrors();
 * ```
 */
export async function getRecording(
  recordingId: string,
  config: BrowserConfig = {}
): Promise<RecordingWithMethods> {
  const apiUrl = config.apiUrl || DEFAULT_CONFIG.apiUrl;
  const debug = config.debug || false;

  if (!config.apiKey) {
    throw new Error('API key required for getRecording');
  }

  if (debug) console.log(`[Browser] getRecording: ${recordingId}`);

  const response = await fetch(`${apiUrl}/recordings/${recordingId}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${config.apiKey}` },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    if (debug) console.error(`[Browser] getRecording error: ${response.status} - ${errorText}`);
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data: RecordingStatus = mapRecordingStatus(await response.json());
  if (debug) console.log(`[Browser] Recording status: ${data.status}`);

  // Return recording with convenience methods
  return {
    ...data,
    getWebp: (options?: WebpOptions) => getWebp(recordingId, config, options),
    getErrors: () => getErrors(recordingId, config),
  };
}

/**
 * Wait for recording to complete with automatic polling
 * 
 * @param recordingId - Recording UUID
 * @param config - Configuration with apiKey
 * @param options - Polling options
 * @returns Recording status when completed or errored
 * 
 * @example
 * ```typescript
 * const result = await executeBrowserTask({ task: '...', recordVideo: true }, config);
 * if (result.recordingId) {
 *   const recording = await waitForRecording(result.recordingId, config, {
 *     timeout: 60000,  // 1 minute
 *     pollInterval: 2000  // Check every 2 seconds
 *   });
 *   console.log('Video URL:', recording.videoUrl);
 * }
 * ```
 */
export async function waitForRecording(
  recordingId: string,
  config: BrowserConfig = {},
  options: { timeout?: number; pollInterval?: number } = {}
): Promise<RecordingWithMethods> {
  const timeout = options.timeout ?? 60000; // Default 1 minute
  const pollInterval = options.pollInterval ?? 2000; // Default 2 seconds
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const status = await getRecording(recordingId, config);
    
    if (status.status === 'COMPLETED' || status.status === 'ERROR') {
      return status;
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }

  throw new Error(`Recording timeout after ${timeout}ms - status still pending`);
}

/**
 * Execute task with recording and wait for video to be ready
 * 
 * @param input - Task parameters with recordVideo=true
 * @param config - Configuration with apiKey
 * @returns Task result with ready video URL
 * 
 * @example
 * ```typescript
 * const result = await executeWithRecording(
 *   {
 *     task: "Test checkout flow",
 *     url: "https://example.com",
 *     recordVideo: true,
 *     repoId: "my-project"
 *   },
 *   { apiKey: process.env.MORPH_API_KEY }
 * );
 * 
 * console.log('Task result:', result.result);
 * console.log('Video URL:', result.recording?.videoUrl);
 * ```
 */
export async function executeWithRecording(
  input: BrowserTaskInput & { recordVideo: true },
  config: BrowserConfig = {}
): Promise<BrowserTaskResult & { recording?: RecordingWithMethods }> {
  // Execute task with recording
  const taskResult = await executeBrowserTask(input, config);

  // If recording was created, wait for it to complete
  if (taskResult.recordingId) {
    try {
      const recording = await waitForRecording(
        taskResult.recordingId,
        config,
        { timeout: 60000, pollInterval: 2000 }
      );
      return {
        ...taskResult,
        recording,
      };
    } catch (error) {
      // Return task result even if recording fails
      // Still attach convenience methods for consistent API
      const errorRecording: RecordingWithMethods = {
        id: taskResult.recordingId,
        status: 'ERROR',
        error: error instanceof Error ? error.message : String(error),
        createdAt: new Date().toISOString(),
        getWebp: (options?: WebpOptions) => getWebp(taskResult.recordingId!, config, options),
        getErrors: () => getErrors(taskResult.recordingId!, config),
      };
      return {
        ...taskResult,
        recording: errorRecording,
      };
    }
  }

  return taskResult;
}

/**
 * Get errors from recording with screenshots
 * 
 * Screenshots are captured in real-time (500ms after error occurs) during the browser session.
 * 
 * @param recordingId - Recording UUID from BrowserTaskResult
 * @param config - Configuration with apiKey
 * @returns Errors with real-time screenshots
 * 
 * @example
 * ```typescript
 * const { errors, totalErrors } = await getErrors('uuid-here', { apiKey: 'key' });
 * 
 * console.log(`Found ${totalErrors} errors`);
 * 
 * errors.forEach(err => {
 *   console.log(`[${err.type}] ${err.message}`);
 *   if (err.url) console.log(`  URL: ${err.url}`);
 *   if (err.screenshotUrl) console.log(`  Screenshot: ${err.screenshotUrl}`);
 *   
 *   // Download screenshot
 *   if (err.screenshotUrl) {
 *     const response = await fetch(err.screenshotUrl);
 *     const screenshot = await response.arrayBuffer();
 *     // Save or process screenshot
 *   }
 * });
 * ```
 */
export async function getErrors(
  recordingId: string,
  config: BrowserConfig = {}
): Promise<ErrorsResponse> {
  const apiUrl = config.apiUrl || DEFAULT_CONFIG.apiUrl;
  const debug = config.debug || false;

  if (!config.apiKey) {
    throw new Error('API key required for getErrors');
  }

  if (debug) console.log(`[Browser] getErrors: ${recordingId}`);

  const response = await fetch(`${apiUrl}/recordings/${recordingId}/errors`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${config.apiKey}` },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    if (debug) console.error(`[Browser] getErrors error: ${response.status} - ${errorText}`);
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const errors = mapErrorsResponse(await response.json());
  if (debug) console.log(`[Browser] Found ${errors.totalErrors} errors`);

  return errors;
}

/**
 * Helper to serialize Zod schema for API
 */
function stringifyStructuredOutput(schema: any): string {
  try {
    return JSON.stringify({
      type: 'object',
      description: 'Zod schema definition (Zod v3)',
      zodDef: schema._def,
    });
  } catch (error) {
    console.warn('[Browser] Failed to serialize Zod schema:', error);
    return JSON.stringify({
      type: 'object',
      description: 'Schema serialization failed',
    });
  }
}

/**
 * Parse and validate structured task output
 */
function parseStructuredTaskOutput<T>(
  result: BrowserTaskResult,
  schema: any
): BrowserTaskResult & { parsed: T | null } {
  if (!result.output) {
    return { ...result, parsed: null };
  }

  try {
    const parsed = JSON.parse(result.output);
    const validated = schema.parse(parsed) as T;
    return { ...result, parsed: validated };
  } catch (error) {
    if (error instanceof SyntaxError) {
      return { ...result, parsed: null };
    }
    throw error;
  }
}

function mapTaskResult(api: any): BrowserTaskResult {
  if (!api || typeof api !== 'object') {
    return api as BrowserTaskResult;
  }

  return {
    success: api.success,
    result: api.result,
    error: api.error,
    stepsTaken: api.steps_taken,
    executionTimeMs: api.execution_time_ms,
    urls: api.urls,
    actionNames: api.action_names,
    errors: api.errors,
    modelActions: api.model_actions,
    isDone: api.is_done,
    actionHistory: api.action_history,
    actionResults: api.action_results,
    hasErrors: api.has_errors,
    numberOfSteps: api.number_of_steps,
    judgement: api.judgement,
    isValidated: api.is_validated,
    replayId: api.replay_id,
    replayUrl: api.replay_url,
    recordingId: api.recording_id,
    recordingStatus: api.recording_status,
    taskId: api.task_id,
    status: api.status,
    output: api.output,
    debugUrl: api.debug_url,
  };
}

function mapRecordingStatus(api: any): RecordingStatus {
  return {
    id: api.id,
    status: api.status,
    replayUrl: api.replay_url,
    networkUrl: api.network_url,
    consoleUrl: api.console_url,
    videoUrl: api.video_url,
    result: api.result,
    totalEvents: api.total_events,
    fileSize: api.file_size,
    duration: api.duration,
    error: api.error,
    createdAt: api.created_at,
  };
}

function mapBrowserError(api: any): ErrorsResponse['errors'][number] {
  return {
    type: api.type,
    message: api.message,
    url: api.url,
    timestamp: api.timestamp,
    screenshotUrl: api.screenshot_url,
    capturedAt: api.captured_at,
    status: api.status,
  };
}

function mapErrorsResponse(api: any): ErrorsResponse {
  return {
    recordingId: api.recording_id,
    totalErrors: api.total_errors,
    errors: Array.isArray(api.errors) ? api.errors.map(mapBrowserError) : [],
  };
}

function mapWebpResponse(api: any): WebpResponse {
  return {
    webpUrl: api.webp_url,
    cached: api.cached,
    width: api.width,
    fps: api.fps,
    maxDuration: api.max_duration,
    fileSize: api.file_size,
    maxSizeMb: api.max_size_mb,
    budgetMet: api.budget_met,
    qualityUsed: api.quality_used,
    attempts: api.attempts,
  };
}

/**
 * Get current task status
 */
async function getTaskStatus(
  taskId: string,
  config: BrowserConfig
): Promise<BrowserTaskResult> {
  const apiUrl = config.apiUrl || DEFAULT_CONFIG.apiUrl;
  const debug = config.debug || false;

  if (debug) console.log(`[Browser] getTaskStatus: ${taskId}`);

  const headers: Record<string, string> = {};
  if (config.apiKey) headers['Authorization'] = `Bearer ${config.apiKey}`;

  const response = await fetch(`${apiUrl}/tasks/${taskId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    if (debug) console.error(`[Browser] getTaskStatus error: ${response.status} - ${errorText}`);
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result: BrowserTaskResult = mapTaskResult(await response.json());
  if (debug) console.log(`[Browser] Task status: ${result.status}`);

  return result;
}

/**
 * Generate live URL for watching task execution in real-time
 */
function generateLiveUrl(taskId: string, config: BrowserConfig): string {
  const apiUrl = config.apiUrl || DEFAULT_CONFIG.apiUrl;
  const baseUrl = apiUrl.replace('/api', '');
  return `${baseUrl}/tasks/${taskId}/live`;
}

/**
 * Poll task until completion
 */
async function pollTaskUntilComplete(
  taskId: string,
  config: BrowserConfig,
  pollConfig: { interval?: number; timeout?: number } = {}
): Promise<BrowserTaskResult> {
  const interval = pollConfig.interval ?? 2000; // 2 seconds
  const timeout = pollConfig.timeout ?? 300000; // 5 minutes
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const status = await getTaskStatus(taskId, config);
    
    if (status.status === 'completed' || status.status === 'failed') {
      return status;
    }

    await new Promise(resolve => setTimeout(resolve, interval));
  }

  throw new Error(`Task polling timeout after ${timeout}ms`);
}

/**
 * Wrap task response with convenience methods
 */
function wrapTaskResponse(
  result: BrowserTaskResult,
  config: BrowserConfig
): BrowserTaskWithPromise {
  const debugUrl = result.debugUrl ?? '';

  const wrapped: BrowserTaskWithPromise = {
    ...result,
    debugUrl,
    taskId: result.taskId || '',
    liveUrl: result.taskId
      ? generateLiveUrl(result.taskId, config)
      : debugUrl,
    complete: async (pollConfig?: { interval?: number; timeout?: number }) => {
      // If we have a taskId, poll task status endpoint
      if (result.taskId) {
        return pollTaskUntilComplete(result.taskId!, config, pollConfig);
      }
      // If we have a recordingId, poll recording status instead
      if (result.recordingId) {
        const recording = await waitForRecording(
          result.recordingId,
          config,
          pollConfig
        );
        // Return a result-like object (recording doesn't have full task result)
        return {
          ...result,
          recordingStatus: recording.status,
        } as BrowserTaskResult;
      }
      // No way to poll completion
      throw new Error('Cannot poll completion: no taskId or recordingId available');
    },
    // Add Steel live session helpers - either functional or error-throwing
    getLiveUrl: debugUrl
      ? (options?: LiveSessionOptions) => buildLiveUrl(debugUrl, options)
      : () => {
          throw new Error(
            'Live sessions not available. Your backend must return a debugUrl in the response. Contact support@morphllm.com if you need help.'
          );
        },
    getLiveIframe: debugUrl
      ? (optionsOrPreset?: string | IframeOptions) => {
          const options = resolvePreset(optionsOrPreset);
          return buildLiveIframe(debugUrl, options);
        }
      : () => {
          throw new Error(
            'Live sessions not available. Your backend must return a debugUrl in the response. Contact support@morphllm.com if you need help.'
          );
        },
    getEmbedCode: debugUrl
      ? () => buildEmbedCode(debugUrl)
      : () => {
          throw new Error(
            'Live sessions not available. Your backend must return a debugUrl in the response. Contact support@morphllm.com if you need help.'
          );
        },
  };

  return wrapped;
}

/**
 * Wrap task response with schema validation
 */
function wrapTaskResponseWithSchema<T>(
  result: BrowserTaskResult,
  config: BrowserConfig,
  schema: any
): BrowserTaskWithPromiseAndSchema<T> {
  const debugUrl = result.debugUrl ?? '';
  const parsed = result.output
    ? parseStructuredTaskOutput<T>(result, schema)
    : { ...result, parsed: null };

  const wrapped: BrowserTaskWithPromiseAndSchema<T> = {
    ...parsed,
    debugUrl,
    taskId: result.taskId || '',
    liveUrl: result.taskId
      ? generateLiveUrl(result.taskId, config)
      : debugUrl,
    complete: async (pollConfig?: { interval?: number; timeout?: number }) => {
      // If we have a taskId, poll task status endpoint
      if (result.taskId) {
        const finalResult = await pollTaskUntilComplete(result.taskId!, config, pollConfig);
        return parseStructuredTaskOutput<T>(finalResult, schema);
      }
      // If we have a recordingId, poll recording status instead
      if (result.recordingId) {
        const recording = await waitForRecording(
          result.recordingId,
          config,
          pollConfig
        );
        // Return parsed result (recording doesn't have task output)
        return {
          ...parsed,
          recordingStatus: recording.status,
        };
      }
      // No way to poll completion
      throw new Error('Cannot poll completion: no taskId or recordingId available');
    },
    // Add Steel live session helpers - either functional or error-throwing
    getLiveUrl: debugUrl
      ? (options?: LiveSessionOptions) => buildLiveUrl(debugUrl, options)
      : () => {
          throw new Error(
            'Live sessions not available. Your backend must return a debugUrl in the response. ' +
            'Contact support@morphllm.com if you need help enabling live sessions. '
          );
        },
    getLiveIframe: debugUrl
      ? (optionsOrPreset?: string | IframeOptions) => {
          const options = resolvePreset(optionsOrPreset);
          return buildLiveIframe(debugUrl, options);
        }
      : () => {
          throw new Error(
            'Live sessions not available. Your backend must return a debugUrl in the response. ' +
            'Contact support@morphllm.com if you need help enabling live sessions.'
          );
        },
    getEmbedCode: debugUrl
      ? () => buildEmbedCode(debugUrl)
      : () => {
          throw new Error(
            'Live sessions not available. Your backend must return a debugUrl in the response. ' +
            'Contact support@morphllm.com if you need help enabling live sessions.'
          );
        },
  };

  return wrapped;
}

/**
 * Get animated WebP preview of recording
 * 
 * Converts the native video recording to an animated WebP. Results are cached in S3.
 * 
 * @param recordingId - Recording UUID from BrowserTaskResult
 * @param config - Configuration with apiKey
 * @param options - WebP generation options
 * @returns WebP URL and metadata
 * 
 * @example
 * ```typescript
 * const webp = await getWebp('uuid-here', { apiKey: 'key' }, {
 *   width: 780,
 *   fps: 10,
 *   quality: 65,
 *   maxDuration: 15
 * });
 * console.log('WebP URL:', webp.webpUrl);
 * console.log('From cache:', webp.cached);
 * ```
 */
export async function getWebp(
  recordingId: string,
  config: BrowserConfig = {},
  options: WebpOptions = {}
): Promise<WebpResponse> {
  const apiUrl = config.apiUrl || DEFAULT_CONFIG.apiUrl;
  const debug = config.debug || false;

  if (!config.apiKey) {
    throw new Error('API key required for getWebp');
  }

  // Build query params
  const params = new URLSearchParams();
  if (options.maxDuration !== undefined) params.set('max_duration', String(options.maxDuration));
  if (options.fps !== undefined) params.set('fps', String(options.fps));
  if (options.width !== undefined) params.set('width', String(options.width));
  if (options.quality !== undefined) params.set('quality', String(options.quality));
  if (options.maxSizeMb !== undefined) params.set('max_size_mb', String(options.maxSizeMb));

  const url = `${apiUrl}/recordings/${recordingId}/webp${params.toString() ? '?' + params.toString() : ''}`;
  
  if (debug) console.log(`[Browser] getWebp: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${config.apiKey}` },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    if (debug) console.error(`[Browser] getWebp error: ${response.status} - ${errorText}`);
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result = mapWebpResponse(await response.json());
  if (debug) console.log(`[Browser] WebP ready: ${result.webpUrl} (cached: ${result.cached})`);

  return result;
}

/**
 * Check if browser worker service is healthy
 * 
 * @param config - Optional configuration
 * @returns Health status
 */
export async function checkHealth(config: BrowserConfig = {}): Promise<{
  ok: boolean;
  google_configured: boolean;
  database_configured: boolean;
  s3_configured: boolean;
  error?: string;
}> {
  const apiUrl = config.apiUrl || DEFAULT_CONFIG.apiUrl;

  try {
    const response = await fetch(`${apiUrl}/health`, {
      method: 'GET',
      headers: config.apiKey
        ? { 'Authorization': `Bearer ${config.apiKey}` }
        : {},
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
      ok: true,
      google_configured: data.google_configured ?? false,
      database_configured: data.database_configured ?? false,
      s3_configured: data.s3_configured ?? false,
    };
  } catch (error) {
    return {
      ok: false,
      google_configured: false,
      database_configured: false,
      s3_configured: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
